import {
  EcommerceAttributeFilterGroup,
  EcommerceFilterOption,
  EcommerceProductFilters,
  SelectCategories,
} from "./../interfaces/categories.interface";
import {
  PurchaseHistoryDetailResponse,
  PurchaseHistoryResponse,
} from "./../interfaces/orders.interface";
import {
  CartItem,
  EcommerceProductDetail,
  OrderRequest,
  Product,
} from "../interfaces/products.interface";
import { endpoint, httpOptions } from "./../apis/endpoint";
import {
  BaseFiltersRequest,
  BaseResponse,
} from "./../models/base-api-response.interface";
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, map, Observable } from "rxjs";
import { environment as env } from "../../../environments/environment";
import { DocumentTypes } from "../interfaces/documentType.interface";

@Injectable({
  providedIn: "root",
})
export class EcommerceService {
  private cartSubject = new BehaviorSubject<CartItem[]>(
    this.getCartFromLocalStorage()
  );
  cart$ = this.cartSubject.asObservable();

  constructor(private _http: HttpClient) {}

  /*************************************************************************/
  /*************************************************************************/
  /*************************************************************************/
  /*********FUNCIONES PARA OBTENER LOS PRODUCTO CON Y SIN FILTRO************/
  /*************************************************************************/
  /*************************************************************************/
  /*************************************************************************/
  GetAll(): Observable<BaseResponse<Product[]>> {
    const requestUrl = `${env.api}${endpoint.ECOMMERCE_PRODUCTS}`;
    return this._http.get<BaseResponse<Product[]>>(requestUrl);
  }

  GetAllFiltered(
    filters: BaseFiltersRequest
  ): Observable<BaseResponse<Product[]>> {
    const requestUrl = `${env.api}${endpoint.ECOMMERCE_PRODUCTS_FILTERS}`;
    const params = Object.entries({ ...filters }).reduce((acc, [key, value]) => {
      if (value === null || value === undefined || value === "") {
        return acc;
      }

      acc[key] = value;
      return acc;
    }, {} as Record<string, string | number | boolean>);

    return this._http.get<BaseResponse<Product[]>>(requestUrl, { params });
  }

  GetProductDetail(
    productId: number,
    priceListId?: number | null,
    clientId?: number | null
  ): Observable<BaseResponse<EcommerceProductDetail>> {
    const requestUrl = `${env.api}${endpoint.ECOMMERCE_PRODUCT_DETAIL}${productId}`;
    const params = Object.entries({ priceListId, clientId }).reduce((acc, [key, value]) => {
      if (value === null || value === undefined) {
        return acc;
      }

      acc[key] = value;
      return acc;
    }, {} as Record<string, string | number | boolean>);

    return this._http.get<BaseResponse<EcommerceProductDetail>>(requestUrl, {
      params: Object.keys(params).length ? params : undefined,
    });
  }

  /*************************************************************************/
  /*************************************************************************/
  /*************************************************************************/
  /*****************FUNCIONES PARA OBTENER LAS CATEGORIAS*******************/
  /*************************************************************************/
  /*************************************************************************/
  /*************************************************************************/

  ListSelectCategories(): Observable<SelectCategories[]> {
    const requestUrl = `${env.api}${endpoint.ECOMMERCE_SELECT_CATEGORIES}`;
    return this._http
      .get<BaseResponse<SelectCategories[]>>(requestUrl)
      .pipe(map((resp) => resp.data));
  }

  GetProductFilters(
    filters: BaseFiltersRequest
  ): Observable<BaseResponse<EcommerceProductFilters>> {
    const requestUrl = `${env.api}${endpoint.ECOMMERCE_PRODUCT_FILTER_OPTIONS}`;
    const params = Object.entries({ ...filters }).reduce((acc, [key, value]) => {
      if (value === null || value === undefined || value === "") {
        return acc;
      }

      acc[key] = value;
      return acc;
    }, {} as Record<string, string | number | boolean>);

    return this._http.get<BaseResponse<EcommerceProductFilters>>(requestUrl, {
      params,
    });
  }

  /*************************************************************************/
  /*************************************************************************/
  /*************************************************************************/
  /**************FUNCIONES PARA OBTENER LOS TIPOS DE DOCUMENTOS*************/
  /*************************************************************************/
  /*************************************************************************/
  /*************************************************************************/

  ListSelectDocumentType(): Observable<DocumentTypes[]> {
    const requestUrl = `${env.api}${endpoint.LIST_DOCUMENT_TYPE}`;
    return this._http
      .get<BaseResponse<DocumentTypes[]>>(requestUrl)
      .pipe(map((resp) => resp.data));
  }

  /*************************************************************************/
  /*************************************************************************/
  /*************************************************************************/
  /**********************FUNCIONES PARA EL CARRITO**************************/
  /*************************************************************************/
  /*************************************************************************/
  /*************************************************************************/
  private getCartFromLocalStorage(): CartItem[] {
    const timestamp = localStorage.getItem("cartTimestamp");
    const now = Date.now();

    if (timestamp && now - Number(timestamp) > 3600000) {
      localStorage.removeItem("cart");
      localStorage.removeItem("cartTimestamp");
      return [];
    }

    return JSON.parse(localStorage.getItem("cart") || "[]");
  }

  calculateItemSubtotal(
    item: Pick<
      CartItem,
      "quantity" | "unitSalePrice" | "ecommerceDiscountType" | "ecommerceBuyQuantity" | "ecommercePayQuantity"
    >
  ): number {
    const quantity = Math.max(0, Number(item.quantity) || 0);
    const price = Number(item.unitSalePrice) || 0;

    if (
      item.ecommerceDiscountType === "BUY_X_GET_Y" &&
      (item.ecommerceBuyQuantity || 0) > 0 &&
      (item.ecommercePayQuantity || 0) >= 0 &&
      (item.ecommerceBuyQuantity || 0) > (item.ecommercePayQuantity || 0)
    ) {
      const buyQty = Number(item.ecommerceBuyQuantity) || 0;
      const payQty = Number(item.ecommercePayQuantity) || 0;
      const promoBundles = Math.floor(quantity / buyQty);
      const remainder = quantity % buyQty;
      return (promoBundles * payQty + remainder) * price;
    }

    return quantity * price;
  }

  // Agregar un producto al carrito
  addToCart(product: Product) {
    const cart = this.getCartFromLocalStorage();
    const existingProductIndex = cart.findIndex(
      (item) => item.productId === product.productId
    );

    if (existingProductIndex > -1) {
      cart[existingProductIndex].quantity++;
      cart[existingProductIndex].subtotal = this.calculateItemSubtotal(cart[existingProductIndex]);
    } else {
      const newProduct: CartItem = {
        productId: product.productId,
        name: product.name,
        unitSalePrice: product.unitSalePrice,
        image: product.image,
        originalUnitSalePrice: product.originalUnitSalePrice ?? null,
        quantity: 1,
        subtotal: product.unitSalePrice,
        hasPromotion: product.hasPromotion ?? false,
        ecommerceBadgeText: product.ecommerceBadgeText ?? null,
        ecommerceBadgeVariant: product.ecommerceBadgeVariant ?? null,
        ecommerceHighlightText: product.ecommerceHighlightText ?? null,
        ecommerceDiscountType: product.ecommerceDiscountType ?? null,
        ecommerceDiscountValue: product.ecommerceDiscountValue ?? null,
        ecommerceBuyQuantity: product.ecommerceBuyQuantity ?? null,
        ecommercePayQuantity: product.ecommercePayQuantity ?? null,
      };
      newProduct.subtotal = this.calculateItemSubtotal(newProduct);
      cart.push(newProduct);
    }

    this.updateCart(cart);
  }

  // Eliminar un producto del carrito
  removeFromCart(productId: string) {
    const cart = this.getCartFromLocalStorage().filter(
      (item: CartItem) => item.productId !== Number(productId)
    );
    this.updateCart(cart);
  }

  // Limpiar el carrito (eliminar todos los productos)
  clearCart() {
    localStorage.removeItem("cart");
    localStorage.removeItem("cartTimestamp");
    this.cartSubject.next([]);
  }

  // Actualizar carrito
  public updateCart(cart: CartItem[]) {
    localStorage.setItem("cart", JSON.stringify(cart));
    localStorage.setItem("cartTimestamp", Date.now().toString());
    this.cartSubject.next(cart);
  }

  // Obtener el total del carrito (suma de subtotales)
  getTotal(): number {
    const cart = this.getCartFromLocalStorage();
    return cart.reduce((total, item) => total + this.calculateItemSubtotal(item), 0);
  }

  // Obtener los productos del carrito
  getCartItems(): any[] {
    return this.getCartFromLocalStorage();
  }

  /*************************************************************************/
  /*************************************************************************/
  /*************************************************************************/
  /*******************FUNCIONES PARA ENVIAR PEDIDO**************************/
  /*************************************************************************/
  /*************************************************************************/
  /*************************************************************************/
  OrderRegister(order: OrderRequest): Observable<BaseResponse<OrderRequest>> {
    const requestUrl = `${env.api}${endpoint.ORDER_REGISTER}`;
    return this._http.post(requestUrl, order, httpOptions).pipe(
      map((resp: BaseResponse<OrderRequest>) => {
        return resp;
      })
    );
  }

  GetEffectivePriceListByClient(clientId: number): Observable<SelectCategories | null> {
    const requestUrl = `${env.api}${endpoint.PRICE_LIST_DEFAULT_BY_CLIENT}${clientId}/Default`;
    return this._http.get<BaseResponse<SelectCategories | null>>(requestUrl).pipe(
      map((resp) => resp.data)
    );
  }

  GetPurchaseHistory(usuario: number,
    filters: BaseFiltersRequest
  ): Observable<BaseResponse<PurchaseHistoryResponse[]>> {
    const requestUrl = `${env.api}${endpoint.CLIENT_PURCHASE_HISTORY}${usuario}/PurchaseHistory`;
    const params = { ...filters } as any;
    return this._http.get<BaseResponse<PurchaseHistoryResponse[]>>(requestUrl, {
      params,
    });
  }

  GetPurchaseHistoryDetail(
    usuario: number,
    sourceType: string,
    sourceId: number
  ): Observable<BaseResponse<PurchaseHistoryDetailResponse>> {
    const requestUrl = `${env.api}${endpoint.CLIENT_PURCHASE_HISTORY}${usuario}/PurchaseHistory/${sourceType}/${sourceId}`;
    return this._http.get<BaseResponse<PurchaseHistoryDetailResponse>>(requestUrl);
  }
}
